import { appAlert } from "./appDialogs";
import { API_ORIGIN, apiUrl } from "./apiOrigin";
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { saveXlsxProtected } from "./protectedExport";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { displayName, useMaskNames, MaskToggleButton } from "./nameMask";

/* ================================
   PROD ENDPOINTS
   ================================ */
const API_BASE = apiUrl("");
const LEGACY_ROUTE = "/getStaffAttendanceLogs"; // legacy per-tenant logs
const BREAK_ROUTE = "/getStaffBreakLogs"; // per-tenant break logs (legacy-style)
const ALLOW_EDIT = false;
const Staff = () => {
  const masked = useMaskNames();
  const [data, setData] = useState([]); // merged legacy + v2 synthetic entries
  const [loading, setLoading] = useState(true); // initial + refresh fetch indicator
  const [breaksByAttId, setBreaksByAttId] = useState({}); // { attendanceLogId: [ {breakStart, breakEnd, notes?} ] }

  // filters
  const [selectedPharmacy, setSelectedPharmacy] = useState("All");
  const [selectedMonth, setSelectedMonth] = useState("All");
  const [selectedYear, setSelectedYear] = useState("All");
  const [selectedStaff, setSelectedStaff] = useState("All");
  const [searchText, setSearchText] = useState("");
  const [showDetails, setShowDetails] = useState(true);
  const [onlyShowIssues, setOnlyShowIssues] = useState(false);

  // sorting
  const [sortBy, setSortBy] = useState("date");
  const [sortDir, setSortDir] = useState("desc"); // reverse chronological by default

  // edit UI (kept intact)
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editSessionIndex, setEditSessionIndex] = useState(-1);
  const [editInDT, setEditInDT] = useState("");
  const [editOutDT, setEditOutDT] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editAddNotes, setEditAddNotes] = useState("");
  const [lastChangeSummary, setLastChangeSummary] = useState("");
  const [editOutEditable, setEditOutEditable] = useState(true);

  // map modal
  const [mapOpen, setMapOpen] = useState(false);
  const [mapInfo, setMapInfo] = useState(null);
  const [mapMode, setMapMode] = useState("in");
  const mapDivRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const mapMarkersRef = useRef(null);

  // hover session card
  const [hoverKey, setHoverKey] = useState(null); // `${rowIdx}:${sessionIdx}`
  const [hoverInfo, setHoverInfo] = useState(null);
  const members = JSON.parse(localStorage.getItem("members") || "[]");
  const organisationName = localStorage.getItem("organisationName") || "Group";

  /* ========== Clock annotations (overtime / approvals / reviews / manager
     notes) — fetched from headoffice v36 /clock/annotations and keyed
     "<pharmacyName>|<staffId>|<YYYY-MM-DD>". Used for the four extra
     columns, the search, and the Excel export. ========== */
  const [anno, setAnno] = useState({
    byKey: {},
    timesByPharmacy: {}
  });
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("token") || localStorage.getItem("authToken") || localStorage.getItem("jwt") || "";
        const res = await fetch(`${API_BASE}/api/headoffice/clock/annotations`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: "{}"
        });
        const d = await res.json();
        if (!res.ok) return;
        const byKey = {};
        const timesByPharmacy = {};
        for (const p of d.pharmacies || []) {
          timesByPharmacy[p.pharmacyName] = p.times || [];
          for (const a of p.overtime || []) {
            const k = `${p.pharmacyName}|${a.staffId}|${a.date}`;
            (byKey[k] ||= {}).otApproved = a.approved;
          }
          for (const a of p.reviews || []) {
            const k = `${p.pharmacyName}|${a.staffId}|${a.date}`;
            const o = byKey[k] ||= {};
            o.reviewed = a.approved;
            o.notes = a.notes || "";
          }
        }
        setAnno({
          byKey,
          timesByPharmacy
        });
      } catch (_) {}
    })();
  }, []);
  const annoFor = (pharmacyName, staffId, dateStr) => anno.byKey[`${pharmacyName}|${staffId}|${dateStr}`] || {};
  const annoPlannedH = (pharmacyName, staffId, dateStr) => {
    const times = anno.timesByPharmacy[pharmacyName] || [];
    const d = new Date(dateStr + "T00:00:00");
    const weekday = d.getDay(),
      occurrence = Math.floor((d.getDate() - 1) / 7) + 1;
    const t = times.find(x => String(x.staffId) === String(staffId) && x.weekday === weekday && x.occurrence === occurrence && x.start && x.end);
    if (!t) return 0;
    const [sh, sm] = t.start.split(":").map(Number),
      [eh, em] = t.end.split(":").map(Number);
    return Math.max(0, (eh * 60 + (em || 0) - (sh * 60 + (sm || 0))) / 60);
  };
  // Same rule as the Staffing panel: on-site minus contracted when
  // contracted is known; a worked day with no contracted hours counts wholly.
  const annoOvertimeH = (pharmacyName, staffId, dateStr, onSiteH) => {
    if (!(onSiteH > 0)) return 0;
    const pl = annoPlannedH(pharmacyName, staffId, dateStr);
    return pl > 0 ? Math.max(0, onSiteH - pl) : onSiteH;
  };

  /* ========== Styles (kept ASCII only) ========== */
  const staffLogsStyles = `
  .staff-logs { width: 100%; border-collapse: collapse; table-layout: fixed; border: 1px solid #cfd8e3; }
  .staff-logs th, .staff-logs td { border: 1px solid #d6dbe5; padding: 8px 10px; text-align: left; vertical-align: top; white-space: pre-line; }
  .staff-logs thead th { background: #EDF2F7; border-bottom: 2px solid #c8d1e0; position: sticky; top: 0; z-index: 1; user-select: none; cursor: pointer; }
  .staff-logs thead th.nosort { cursor: default; }
  .staff-logs tbody tr:nth-child(odd) td { background-color: #fbfbfd; }
  .staff-logs td { word-break: break-word; }

  .seg { border-top: 1px dashed #c9cdd3 !important; }
  .edit-icon-btn { border: none; background: transparent; cursor: pointer; font-size: 12px; padding: 0 6px; color: #0E8CFF; }
  .edit-icon-btn:hover { text-decoration: underline; }

  .amber { background: #ffe8a3; }
  .nk { background: #ffe599; }

  .sort-ind { font-size: 11px; color: #5D5D5D; margin-left: 6px; }
  .th-wrap { display:flex; align-items:center; }
  .th-wrap .label { pointer-events: none; }

  .divider-row td { text-align: center; font-weight: 600; color: #434343; background: #FAFAFA; border-top: 2px solid #262626 !important; border-bottom: 2px solid #262626 !important; }

  .dist-cell { cursor: pointer; text-decoration: underline; }
  .dist-green { background: lightgreen !important; }
  .dist-coral { background: lightcoral !important; }

  .staff-logs tr.row-invalid td { background: lightcoral !important; }

  .modal-backdrop, .map-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.35); display: flex; align-items: center; justify-content: center; z-index: 9999; }
  .modal-card, .map-card { width: 760px; max-width: 96vw; background: #fff; border-radius: 8px; box-shadow: 0 20px 50px rgba(0,0,0,0.25); overflow: hidden; }
  .modal-header, .map-header { display:flex; align-items:center; justify-content:space-between; padding: 10px 12px; border-bottom: 1px solid #F0F0F0; background: #FAFAFA; }
  .modal-title, .map-title { margin: 0; font-size: 16px; font-weight: 700; }
  .modal-body, .map-body { padding: 12px; }
  .modal-footer, .map-footer { padding: 12px; border-top: 1px solid #F0F0F0; display:flex; justify-content:flex-end; gap: 8px; }
  .btn { padding: 6px 10px; border: 1px solid #D9D9D9; border-radius: 6px; background: #fff; cursor: pointer; }
  .btn.primary { background: #0E8CFF; color: #fff; border-color: #0958D9; }

  .modal-header .btn, .map-header .btn { background: #262626; color: #fff; border-color: #262626; opacity: 1; }
  .modal-header .btn:hover, .map-header .btn:hover { filter: brightness(0.92); }

  #attendance-map { width: 100%; height: 420px; border: 1px solid #F0F0F0; border-radius: 6px; }
  .pharmacy-marker { font-size: 18px; line-height: 20px; text-align: center; }

  .report-wrap { font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif; padding: 16px; }
  .report-title { margin:0; font-size:20px; font-weight:700; }
  .report-meta { font-size:12px; color:#555; }
  .filters-line { font-size:12px; color:#444; margin-bottom:8px; }
  .report-table { width: 100%; border-collapse: separate; border-spacing: 18px 8px; }
  .report-table th, .report-table td { border: 1px solid #d6dbe5; padding: 10px 12px; vertical-align: top; }
  .staff-divider { text-align:center; font-weight:700; margin: 10px 0 22px 0; }
  .spacer-row { height: 8px; }

  /* Hover card for session details */
  .session-wrap { position: relative; display: inline-block; }
  .hover-card {
    position: absolute;
    left: 0;
    top: 100%;
    margin-top: 6px;
    width: 360px;
    max-width: 80vw;
    background: #ffffff;
    border: 1px solid #F0F0F0;
    border-radius: 8px;
    padding: 10px 12px;
    box-shadow: 0 12px 30px rgba(0,0,0,0.18);
    z-index: 50;
  }
  .hover-row { display: flex; justify-content: space-between; gap: 10px; font-size: 13px; line-height: 1.35; }
  .hover-row + .hover-row { margin-top: 6px; }
  .hover-title { font-weight: 700; }
  .hover-note { margin-top: 6px; font-size: 12px; color: #434343; white-space: pre-wrap; }
  .hover-total { margin-top: 8px; padding-top: 8px; border-top: 1px dashed #D9D9D9; font-size: 13px; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  `;

  /* ========== Fetch (legacy + v2 + breaks) ========== */
  const fetchAllLogs = useCallback(async () => {
    setLoading(true);
    const authToken = localStorage.getItem("authToken") || localStorage.getItem("token");
    const headers = {
      "Content-Type": "application/json",
      ...(authToken ? {
        Authorization: "Bearer " + authToken
      } : {})
    };
    try {
      const groupId = localStorage.getItem("userId");
      if (!groupId) {
        console.warn("No groupId found in localStorage");
        return;
      }
      console.log("FETCH staff logs payload", {
        groupId,
        authToken: Boolean(authToken),
        members
      });
      const [legacyRes, brRes] = await Promise.all([fetch(API_BASE + LEGACY_ROUTE, {
        method: "POST",
        headers,
        body: JSON.stringify({
          groupId
        })
      }), fetch(API_BASE + BREAK_ROUTE, {
        method: "POST",
        headers,
        body: JSON.stringify({
          groupId
        })
      })]);
      const legacyText = await legacyRes.text();
      const breaksText = await brRes.text();
      if (!legacyRes.ok) console.error("getStaffAttendanceLogs failed:", legacyRes.status, legacyText);
      if (!brRes.ok) console.error("getStaffBreakLogs failed:", brRes.status, breaksText);
      const legacyLogs = legacyText ? JSON.parse(legacyText).logs || [] : [];
      const breaksArr = breaksText ? JSON.parse(breaksText).breaks || [] : [];
      console.log("RAW legacy logs count:", legacyLogs.length);
      console.log("RAW legacy sample:", legacyLogs.slice(0, 5));
      console.log("RAW legacy createdAt range:", legacyLogs.map(l => l.createdAt).sort()[0], "→", legacyLogs.map(l => l.createdAt).sort().at(-1));
      console.log("RAW breaks logs count:", breaksArr.length);
      console.log("RAW breaks sample:", breaksArr.slice(0, 5));
      console.log("RAW breaks time range:", breaksArr.map(b => b.breakStart || b.createdAt).filter(Boolean).sort()[0], "→", breaksArr.map(b => b.breakEnd || b.createdAt).filter(Boolean).sort().at(-1));
      console.log("Breaks with attendanceLogId:", breaksArr.filter(b => b.attendanceLogId || b.attendance_log_id).length);
      console.log("Sample break attendance IDs:", breaksArr.map(b => b.attendanceLogId || b.attendance_log_id).filter(Boolean).slice(0, 10));

      // Build breaks map by attendanceLogId
      const map = {};
      for (const b of breaksArr) {
        const key = String(b.attendanceLogId || b.attendance_log_id || "");
        if (!key) continue;
        (map[key] ||= []).push({
          breakStart: b.breakStart || null,
          breakEnd: b.breakEnd || null,
          notes: b.notes || ""
        });
      }
      setData(Array.isArray(legacyLogs) ? legacyLogs : []);
      setBreaksByAttId(map);
      console.log("Breaks map keys (attendanceLogId):", Object.keys(map).slice(0, 10));
      const firstKey = Object.keys(map)[0];
      if (firstKey) {
        console.log("Sample breaks for first attendanceLogId:", firstKey, map[firstKey]);
      }
    } catch (err) {
      console.error("Error fetching staff logs:", err);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    fetchAllLogs();
  }, [fetchAllLogs]);

  /* ========== Anchored-month helpers ========== */
  const getTimesheetDay = () => {
    const raw = localStorage.getItem("timesheet_day") || localStorage.getItem("timesheetDay") || "1";
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n >= 1 && n <= 28 ? n : 1;
  };
  const clampStartOfDay = (y, m, d) => {
    const last = new Date(y, m + 1, 0).getDate();
    const dd = Math.max(1, Math.min(d, last));
    return new Date(y, m, dd, 0, 0, 0, 0);
  };
  const anchoredWindowForSelection = (yearNum, monthIdx, reportDay) => {
    const start = clampStartOfDay(yearNum, monthIdx - 1, reportDay);
    const end = clampStartOfDay(yearNum, monthIdx, reportDay);
    return {
      start,
      end
    };
  };

  /* ========== Filter for PAIRING (buffered) ========== */
  const BUFFER_MS = 2 * 24 * 3600 * 1000; // +/- 2 days

  const windows = useMemo(() => {
    if (selectedYear === "All" || selectedMonth === "All") return null;
    const yearNum = parseInt(selectedYear, 10);
    const monthIdx = parseInt(selectedMonth, 10);
    const reportDay = getTimesheetDay();
    const {
      start,
      end
    } = anchoredWindowForSelection(yearNum, monthIdx, reportDay);
    const startBuffered = new Date(start.getTime() - BUFFER_MS);
    const endBuffered = new Date(end.getTime() + BUFFER_MS);
    return {
      start,
      end,
      startBuffered,
      endBuffered
    };
  }, [selectedYear, selectedMonth]);
  const allowTotals = selectedStaff !== "All" && selectedMonth !== "All" && selectedYear !== "All";

  // Raw items enriched with searchable annotation strings so the search box
  // finds "reviewed", "overtime approved", overtime figures and manager
  // notes just like any other field.
  const annotatedData = useMemo(() => {
    const dayOn = {};
    for (const it of data) {
      if (it.clockIn && it.clockOut) {
        const a = new Date(it.clockIn),
          b = new Date(it.clockOut);
        if (!isNaN(a) && !isNaN(b) && a.toISOString().slice(0, 10) === b.toISOString().slice(0, 10)) {
          const k = `${it.pharmacyName || ""}|${String(it.staffId || "")}|${a.toISOString().slice(0, 10)}`;
          dayOn[k] = (dayOn[k] || 0) + Math.max(0, (b - a) / 3600000);
        }
      }
    }
    return data.map(it => {
      const anchor = it.clockIn || it.createdAt || it.clockOut;
      const dateStr = anchor ? new Date(anchor).toISOString().slice(0, 10) : "";
      const ph = it.pharmacyName || "",
        sid = String(it.staffId || "");
      const a = annoFor(ph, sid, dateStr);
      const ot = annoOvertimeH(ph, sid, dateStr, dayOn[`${ph}|${sid}|${dateStr}`] || 0);
      return {
        ...it,
        __reviewedTxt: a.reviewed ? "reviewed yes" : "",
        __reviewNotes: a.notes || "",
        __otApprovedTxt: a.otApproved ? "overtime approved yes" : "",
        __overtimeTxt: ot > 0 ? `overtime ${ot.toFixed(2)}h` : ""
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, anno]);
  const baseFilteredForPairing = useMemo(() => {
    return annotatedData.filter(item => {
      if (selectedPharmacy !== "All" && item.pharmacyName !== selectedPharmacy) return false;
      if (windows) {
        const created = item.createdAt ? new Date(item.createdAt) : null;
        if (!created || isNaN(created)) return false;
        if (!(created >= windows.startBuffered && created < windows.endBuffered)) return false;
      }
      if (searchText) {
        const hit = Object.values(item).some(val => val && val.toString().toLowerCase().includes(searchText.toLowerCase()));
        if (!hit) return false;
      }
      return true;
    });
  }, [annotatedData, selectedPharmacy, windows, searchText]);
  const staffNameOptions = useMemo(() => {
    return Array.from(new Set(baseFilteredForPairing.map(x => x.staffName || "Unknown"))).sort((a, b) => a.localeCompare(b));
  }, [baseFilteredForPairing]);
  const filteredData = useMemo(() => baseFilteredForPairing.filter(item => selectedStaff === "All" ? true : (item.staffName || "Unknown") === selectedStaff), [baseFilteredForPairing, selectedStaff]);

  /* ========== Utils ========== */
  const toNumOrNull = v => {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const haversineMeters = (lat1, lon1, lat2, lon2) => {
    const a1 = toNumOrNull(lat1),
      b1 = toNumOrNull(lon1);
    const a2 = toNumOrNull(lat2),
      b2 = toNumOrNull(lon2);
    if (a1 === null || b1 === null || a2 === null || b2 === null) return null;
    const R = 6371000;
    const toRad = deg => deg * Math.PI / 180;
    const dLat = toRad(a2 - a1);
    const dLon = toRad(b2 - b1);
    const la1 = toRad(a1),
      la2 = toRad(a2);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLon / 2) ** 2;
    return Math.round(2 * R * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h)));
  };
  const fmtHM = dLike => {
    if (!dLike) return "";
    const d = dLike instanceof Date ? dLike : new Date(dLike);
    if (isNaN(d)) return "";
    return d.toISOString().slice(11, 16);
  };
  const hoursDiff = (a, b) => {
    if (!a || !b) return 0;
    const A = a instanceof Date ? a : new Date(a);
    const B = b instanceof Date ? b : new Date(b);
    const ms = B - A;
    return Number.isFinite(ms) && ms > 0 ? +(ms / 36e5).toFixed(4) : 0;
  };

  /* ========== Build rows (legacy preserved) + breaks integration ========== */
  const rows = useMemo(() => {
    const logsByStaff = {};
    filteredData.slice().sort((a, b) => {
      const sa = a.staffName || "",
        sb = b.staffName || "";
      if (sa !== sb) return sa.localeCompare(sb);
      return new Date(a.createdAt) - new Date(b.createdAt);
    }).forEach(it => {
      const staff = it.staffName || "Unknown";
      (logsByStaff[staff] ||= []).push(it);
    });
    const outRows = [];
    Object.entries(logsByStaff).forEach(([staffName, items]) => {
      const sessions = items.map(e => ({
        in: e,
        out: e
      }));
      const byDate = new Map();
      for (const s of sessions) {
        const anchor = s.in?.clockIn ? new Date(s.in.clockIn) : s.out?.clockOut ? new Date(s.out.clockOut) : new Date(s.in?.createdAt || Date.now());
        if (windows) {
          if (!(anchor >= windows.start && anchor < windows.end)) continue;
        }
        const dateStr = new Date(anchor).toISOString().slice(0, 10);
        (byDate.get(dateStr) || byDate.set(dateStr, []).get(dateStr)).push(s);
      }
      const dateKeys = Array.from(byDate.keys()).sort((a, b) => new Date(a) - new Date(b));
      let runningTotal = 0;
      let anyNKOverall = false;
      dateKeys.forEach(dateStr => {
        const daySessions = byDate.get(dateStr) || [];
        const dayOfWeek = new Date(dateStr).toLocaleString("en-GB", {
          weekday: "short"
        });
        const sessionsText = [];
        const hrsPerSession = [];
        const notesBlocks = [];
        const notesEditedFlags = [];
        const distPairs = [];
        const discrepancyFlags = [];
        let dayTotal = 0;
        let dayHasNK = false;
        let dayOnsiteH = 0; // in->out time for the overtime rule

        daySessions.forEach(({
          in: entryIn,
          out: entryOut
        }) => {
          const inTime = entryIn?.clockIn ? new Date(entryIn.clockIn) : null;
          const outTime = entryOut?.clockOut ? new Date(entryOut.clockOut) : null;
          const inStr = inTime ? fmtHM(inTime) : "00:00";
          const baseSession = outTime ? `${inStr}-${fmtHM(outTime)}` : `${inStr} - Clock out awaited`;
          const attId = String(entryIn?._id || entryOut?._id || "");
          const brs = attId && breaksByAttId[attId] ? breaksByAttId[attId] : [];
          let brHours = 0;
          const brParts = [];
          for (const b of brs) {
            const bs = b.breakStart ? new Date(b.breakStart) : null;
            const be = b.breakEnd ? new Date(b.breakEnd) : null;
            if (bs && be) brHours += hoursDiff(bs, be);
            brParts.push(`${bs ? fmtHM(bs) : "?"}-${be ? fmtHM(be) : "?"}`);
          }
          const brTxt = brParts.length ? ` (breaks: ${brParts.join(", ")}${brHours > 0 ? ", " + brHours.toFixed(2) + "h" : ""})` : "";
          const sessionLabel = baseSession + brTxt;
          sessionsText.push(sessionLabel);
          let durationStr = "n/k";
          let discrepancy = false;
          if (inTime && outTime) {
            const inDay = inTime.toISOString().slice(0, 10);
            const outDay = outTime.toISOString().slice(0, 10);
            if (inDay !== outDay) {
              durationStr = "Clock out day discrepancy";
              discrepancy = true;
              anyNKOverall = true;
            } else {
              dayOnsiteH += Math.max(0, hoursDiff(inTime, outTime));
              let onsite = hoursDiff(inTime, outTime);
              let worked = Math.max(0, onsite - brHours);
              durationStr = worked.toFixed(2);
              dayTotal += worked;
              runningTotal += worked;
            }
          } else {
            dayHasNK = true;
            anyNKOverall = true;
          }
          hrsPerSession.push(durationStr);
          discrepancyFlags.push(discrepancy);
          const lines = [...(entryIn?.clockInnotes ? String(entryIn.clockInnotes).split("\n") : []), ...(entryOut?.clockOutnotes ? String(entryOut.clockOutnotes).split("\n").map(n => "... " + n) : [])].map(s => s.trim()).filter(Boolean);
          const deduped = Array.from(new Set(lines)).join("\n");
          notesBlocks.push(deduped);
          notesEditedFlags.push(/\b\d{2}\/\d{2}\/\d{4},\s*\d{2}:\d{2}:\d{2}\b/.test(deduped));
          const phLat = toNumOrNull(entryIn?.pharmacyLat ?? entryOut?.pharmacyLat);
          const phLong = toNumOrNull(entryIn?.pharmacyLong ?? entryOut?.pharmacyLong);
          const inDist = entryIn && phLat != null && phLong != null && entryIn.clockInlat != null && entryIn.clockInlong != null ? haversineMeters(entryIn.clockInlat, entryIn.clockInlong, phLat, phLong) : null;
          const outDist = entryOut && phLat != null && phLong != null && entryOut.clockOutlat != null && entryOut.clockOutlong != null ? haversineMeters(entryOut.clockOutlat, entryOut.clockOutlong, phLat, phLong) : null;
          distPairs.push({
            in: inDist,
            out: outDist
          });
        });
        const pharmacyName = daySessions[0]?.in?.pharmacyName || daySessions[0]?.out?.pharmacyName || "";
        const rowStaffId = String(daySessions.map(({
          in: eIn,
          out: eOut
        }) => eIn?.staffId || eOut?.staffId).find(Boolean) || "");
        const rowPh = daySessions.map(({
          in: eIn,
          out: eOut
        }) => eIn?.pharmacyName || eOut?.pharmacyName).find(Boolean) || "";
        const rowAnno = annoFor(rowPh, rowStaffId, dateStr);
        const rowOtH = dayHasNK ? null : annoOvertimeH(rowPh, rowStaffId, dateStr, dayOnsiteH);
        outRows.push({
          staffName,
          pharmacyName,
          date: dateStr,
          dayOfWeek,
          sessions: sessionsText,
          hoursWorkedPerSession: hrsPerSession,
          notesBlocks,
          notesEditedFlags,
          distancePairs: distPairs,
          discrepancyFlags,
          incomplete: dayHasNK,
          totalHours: dayHasNK ? "n/k" : dayTotal.toFixed(2),
          overtimeH: rowOtH != null && rowOtH > 0 ? rowOtH.toFixed(2) : dayHasNK ? "n/k" : "",
          otApproved: !!rowAnno.otApproved,
          reviewed: !!rowAnno.reviewed,
          reviewNotes: rowAnno.notes || "",
          rawEntries: daySessions.map(({
            in: entryIn,
            out: entryOut
          }) => entryIn || entryOut)
        });
      });
      if (allowTotals) {
        outRows.push({
          staffName,
          pharmacyName: "",
          date: "Totals",
          dayOfWeek: "",
          sessions: [],
          hoursWorkedPerSession: [],
          notesBlocks: [],
          notesEditedFlags: [],
          distancePairs: [],
          discrepancyFlags: [],
          incomplete: anyNKOverall,
          totalHours: anyNKOverall ? "n/k" : runningTotal.toFixed(2),
          rawEntries: []
        });
      }
    });
    return outRows;
  }, [filteredData, anno, windows, allowTotals, breaksByAttId]);

  /* ========== Sort / Visible ========== */
  const detailRows = rows.filter(r => r.date !== "Totals");
  const totalRows = rows.filter(r => r.date === "Totals");
  const cmp = (a, b) => {
    let av, bv;
    if (sortBy === "date") {
      av = new Date(a.date).getTime();
      bv = new Date(b.date).getTime();
    } else if (sortBy === "pharmacyName") {
      av = (a.pharmacyName || "").toLowerCase();
      bv = (b.pharmacyName || "").toLowerCase();
    } else {
      av = (a.staffName || "").toLowerCase();
      bv = (b.staffName || "").toLowerCase();
    }
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    if (a.staffName !== b.staffName) return a.staffName < b.staffName ? -1 : 1;
    if (a.date !== b.date) return new Date(a.date) - new Date(b.date);
    return 0;
  };
  if (selectedStaff !== "All") {
    detailRows.sort(cmp);
  }
  const isNumericString = s => {
    if (s === null || s === undefined) return false;
    const t = String(s).trim();
    return /^-?\d+(\.\d+)?$/.test(t);
  };
  const hoursIsValid = h => isNumericString(h) && parseFloat(h) > 0;
  const isInvalidHoursVal = h => !hoursIsValid(h);
  const rowHasInvalidHours = r => {
    if (r.date === "Totals") return isInvalidHoursVal(r.totalHours);
    return (r.hoursWorkedPerSession || []).some(isInvalidHoursVal);
  };
  const rowHasNotes = r => {
    const arr = r.notesBlocks || [];
    for (const s of arr) {
      if (String(s || "").trim() !== "") return true;
    }
    return false;
  };
  let visibleRows = showDetails ? rows.slice() : totalRows.slice();
  if (onlyShowIssues) visibleRows = visibleRows.filter(r => rowHasInvalidHours(r) || rowHasNotes(r));

  /* ========== Helpers ========== */
  const monthName = selectedMonth === "All" ? "All months" : new Date(0, parseInt(selectedMonth || "0", 10)).toLocaleString("default", {
    month: "long"
  });
  const yearLabel = selectedYear === "All" ? "All years" : selectedYear;
  const now = new Date();
  const generatedAt = now.toLocaleString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
  const matchesOriginal = (x, orig) => {
    const idMatch = Boolean(orig?._id && x?._id && String(x._id) === String(orig._id));
    const keyMatch = Boolean(!!orig?.createdAt && x.createdAt === orig.createdAt && x.staffName === orig.staffName && x.pharmacyName === orig.pharmacyName);
    return Boolean(idMatch || keyMatch);
  };
  const handleSortClick = key => {
    if (selectedStaff === "All") return;
    if (sortBy === key) setSortDir(d => d === "asc" ? "desc" : "asc");else {
      setSortBy(key);
      setSortDir("asc");
    }
  };
  const sortArrow = key => sortBy === key ? <span className="sort-ind">{sortDir === "asc" ? "▲" : "▼"}</span> : null;

  /* ========== Map rendering ========== */
  const loadLeafletOnce = () => new Promise((resolve, reject) => {
    if (window.L) return resolve(window.L);
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
    const script = document.createElement("script");
    script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    script.onload = () => resolve(window.L);
    script.onerror = reject;
    document.head.appendChild(link);
    document.body.appendChild(script);
  });
  const renderMapMarkers = () => {
    if (!mapInstanceRef.current || !mapInfo || !window.L) return;
    const L = window.L;
    if (mapMarkersRef.current) {
      mapMarkersRef.current.remove();
      mapMarkersRef.current = null;
    }
    const group = L.layerGroup().addTo(mapInstanceRef.current);
    mapMarkersRef.current = group;
    const pharmacyIcon = L.divIcon({
      className: "pharmacy-marker",
      html: "🏠",
      iconSize: [20, 20],
      iconAnchor: [10, 10]
    });
    const ph = L.marker([mapInfo.pharmacyLat, mapInfo.pharmacyLong], {
      icon: pharmacyIcon,
      title: "Pharmacy"
    }).addTo(group);
    ph.bindTooltip("Pharmacy", {
      permanent: true,
      direction: "top",
      offset: [0, -6]
    });
    const bounds = L.latLngBounds([[mapInfo.pharmacyLat, mapInfo.pharmacyLong]]);
    if (mapMode === "in" && mapInfo.inLat != null && mapInfo.inLong != null) {
      const mIn = L.marker([mapInfo.inLat, mapInfo.inLong]).addTo(group);
      mIn.bindTooltip("IN " + (mapInfo.inWhen || "—"), {
        permanent: true,
        direction: "top",
        offset: [0, -6]
      });
      bounds.extend([mapInfo.inLat, mapInfo.inLong]);
    }
    if (mapMode === "out" && mapInfo.outLat != null && mapInfo.outLong != null) {
      const mOut = L.marker([mapInfo.outLat, mapInfo.outLong]).addTo(group);
      mOut.bindTooltip("OUT " + (mapInfo.outWhen || "—"), {
        permanent: true,
        direction: "top",
        offset: [0, -6]
      });
      bounds.extend([mapInfo.outLat, mapInfo.outLong]);
    }
    mapInstanceRef.current.fitBounds(bounds.pad(0.25));
  };
  const openMapForSession = async (row, sessionIdx) => {
    if (!row || row.date === "Totals") return;
    const eIn = row.rawEntries[sessionIdx] || null;
    const eOut = row.rawEntries[sessionIdx] || null;
    const phLat = toNumOrNull(eIn?.pharmacyLat ?? eOut?.pharmacyLat);
    const phLong = toNumOrNull(eIn?.pharmacyLong ?? eOut?.pharmacyLong);
    const inLat = toNumOrNull(eIn?.clockInlat);
    const inLong = toNumOrNull(eIn?.clockInlong);
    const outLat = toNumOrNull(eOut?.clockOutlat);
    const outLong = toNumOrNull(eOut?.clockOutlong);
    if (phLat == null || phLong == null) {
      (await appAlert("Missing pharmacy coordinates."));
      return;
    }
    if (inLat == null && outLat == null) {
      (await appAlert("No staff coordinates for this session."));
      return;
    }
    const inWhen = eIn?.clockIn ? fmtHM(eIn.clockIn) : "—";
    const outWhen = eOut?.clockOut ? fmtHM(eOut.clockOut) : "—";
    setMapInfo({
      pharmacyLat: phLat,
      pharmacyLong: phLong,
      inLat,
      inLong,
      outLat,
      outLong,
      inWhen,
      outWhen,
      label: displayName(row.staffName, masked) + " — " + new Date(row.date).toLocaleDateString("en-GB") + " (" + row.dayOfWeek + ")"
    });
    setMapMode(inLat != null ? "in" : "out");
    setMapOpen(true);
  };
  useEffect(() => {
    if (!mapOpen) return;
    (async () => {
      try {
        await loadLeafletOnce();
        if (!mapDivRef.current) return;
        if (!mapInstanceRef.current) {
          const map = window.L.map(mapDivRef.current);
          mapInstanceRef.current = map;
          window.L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
            maxZoom: 19,
            attribution: "&copy; OpenStreetMap contributors"
          }).addTo(map);
          requestAnimationFrame(() => {
            if (!mapInstanceRef.current) return;
            mapInstanceRef.current.invalidateSize();
            renderMapMarkers();
          });
        } else {
          renderMapMarkers();
        }
      } catch (e) {
        console.error("Failed to init map:", e);
      }
    })();
  }, [mapOpen, mapMode, mapInfo]);
  useEffect(() => {
    if (!mapOpen && mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
      mapMarkersRef.current = null;
    }
  }, [mapOpen]);

  /* ========== Export helpers ========== */
  const computeStaffTotals = staffRows => {
    let hasNK = false;
    let sum = 0;
    for (const row of staffRows) {
      const arr = row.hoursWorkedPerSession || [];
      for (const h of arr) {
        if (!hoursIsValid(h)) {
          hasNK = true;
          continue;
        }
        const n = Number(h);
        if (Number.isFinite(n)) sum += n;
      }
    }
    return {
      hasNK,
      totalStr: hasNK ? "n/k" : sum.toFixed(2)
    };
  };

  /* ========== Export: Word (unchanged layout) ========== */
  const buildReportHTML = () => {
    const title = "Staff Attendance Logs — " + organisationName;
    const filtersLine = [selectedPharmacy === "All" ? "Pharmacy: All Pharmacies" : "Pharmacy: " + selectedPharmacy, "Month: " + monthName, "Year: " + yearLabel, selectedStaff === "All" ? null : "Staff: " + selectedStaff, searchText ? 'Search: "' + searchText + '"' : null, showDetails ? "Detail: full" : "Detail: totals only", onlyShowIssues ? "Filter: only issues" : null].filter(Boolean).join("   |   ");
    const rowsByStaff = {};
    detailRows.forEach(r => {
      (rowsByStaff[r.staffName] ||= []).push(r);
    });
    const tableBlocks = Object.entries(rowsByStaff).sort(([a], [b]) => a.localeCompare(b)).map(([staff, staffRows]) => {
      const body = staffRows.map(row => {
        const n = Math.max(1, (row.sessions || []).length);
        const subs = [];
        for (let i = 0; i < n; i++) {
          const s = row.sessions[i] ?? "";
          const h = row.hoursWorkedPerSession[i] ?? "";
          const notesHTML = (row.notesBlocks[i] || "").replace(/\n/g, "<br>");
          const pair = row.distancePairs?.[i] || {
            in: null,
            out: null
          };
          const txt = "In: " + (pair.in == null ? "—" : pair.in + " m") + "   Out: " + (pair.out == null ? "—" : pair.out + " m");
          const segBorder = i > 0 ? "border-top:1px dashed #c9cdd3;" : "";
          const alertStyle = !hoursIsValid(h) ? "background:lightcoral;" : "";
          subs.push(`
              <tr>
                ${i === 0 ? '<td rowspan="' + n + '">' + new Date(row.date).toLocaleDateString("en-GB") + ' (' + row.dayOfWeek + ')</td>' : ""}
                ${i === 0 ? '<td rowspan="' + n + '">' + (row.pharmacyName || "") + '</td>' : ""}
                ${i === 0 ? '<td rowspan="' + n + '">' + (row.staffName || "") + '</td>' : ""}
                <td style="${segBorder}">${s}</td>
                <td style="${segBorder}${alertStyle}">${h}</td>
                <td style="${segBorder}${row.notesEditedFlags[i] ? 'background:#ffe8a3;border:1px solid #d6dbe5;' : ''}">${notesHTML}</td>
                <td style="${segBorder}">${txt}</td>
              </tr>
            `);
        }
        return subs.join("");
      }).join("");
      let totalsRow = "";
      if (allowTotals) {
        const {
          totalStr
        } = computeStaffTotals(staffRows);
        totalsRow = `
            <tr class="spacer-row"><td colspan="7" style="border:none;"></td></tr>
            <tr>
              <td>Totals</td>
              <td></td>
              <td>${staff || ""}</td>
              <td></td>
              <td>${totalStr}</td>
              <td></td>
              <td></td>
            </tr>
          `;
      }
      return `
          <table class="report-table">
            <colgroup>
              <col style="width: 160px" />
              <col style="width: 180px" />
              <col style="width: 220px" />
              <col style="width: 200px" />
              <col style="width: 130px" />
              <col style="width: 320px" />
              <col style="width: 220px" />
            </colgroup>
            <thead>
              <tr>
                <th>Date</th>
                <th>Pharmacy</th>
                <th>Name</th>
                <th>Sessions</th>
                <th>Hrs worked</th>
                <th>Notes</th>
                <th>Distance</th>
              </tr>
            </thead>
            <tbody>
              ${body || '<tr><td colspan="7">No data.</td></tr>'}
              ${totalsRow}
            </tbody>
          </table>
          <div class="staff-divider">—— End of ${staff} entry ——</div>
        `;
    }).join("");
    return `
<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${title}</title>
<style>${staffLogsStyles}</style>
</head>
<body>
  <div class="report-wrap">
    <div style="display:flex; align-items:flex-end; justify-content:space-between; gap:16px; margin-bottom:12px;">
      <h1 class="report-title">${title}</h1>
      <div class="report-meta">Generated at: ${generatedAt}</div>
    </div>
    <div class="filters-line">${filtersLine}</div>
    ${tableBlocks || "<p>No data.</p>"}
  </div>
</body>
</html>`;
  };
  const exportToWord = () => {
    const html = buildReportHTML();
    const filename = `Staff_Attendance_${organisationName}_${monthName}_${yearLabel}.doc`;
    const blob = new Blob([html], {
      type: "application/msword;charset=utf-8"
    });
    saveAs(blob, filename);
  };

  /* ========== Export: PDF (unchanged) ========== */
  const exportToPDF = () => {
    const rowsByStaff = {};
    detailRows.forEach(r => {
      (rowsByStaff[r.staffName] ||= []).push(r);
    });
    const flat = [];
    Object.entries(rowsByStaff).sort(([a], [b]) => a.localeCompare(b)).forEach(([staff, staffRows]) => {
      staffRows.forEach(row => {
        const n = Math.max(1, row.sessions.length);
        for (let i = 0; i < n; i++) {
          const pair = row.distancePairs?.[i] || {
            in: null,
            out: null
          };
          const distTxt = "In: " + (pair.in == null ? "—" : pair.in + " m") + "  Out: " + (pair.out == null ? "—" : pair.out + " m");
          flat.push({
            date: new Date(row.date).toLocaleDateString("en-GB") + " (" + row.dayOfWeek + ")",
            pharmacy: row.pharmacyName || "",
            name: row.staffName || "",
            session: row.sessions[i] || "",
            hours: row.hoursWorkedPerSession[i] || "",
            notes: (row.notesBlocks[i] || "") + (distTxt ? "\n[" + distTxt + "]" : ""),
            _type: "regular"
          });
        }
      });
      if (allowTotals) {
        const {
          totalStr
        } = computeStaffTotals(staffRows);
        flat.push({
          date: "Totals",
          pharmacy: "",
          name: staff || "",
          session: "",
          hours: totalStr,
          notes: "",
          _type: "totals"
        });
      }
      flat.push({
        date: "—— End of " + staff + " entry ——",
        pharmacy: "",
        name: "",
        session: "",
        hours: "",
        notes: "",
        _type: "divider"
      });
    });
    const doc = new jsPDF({
      unit: "pt",
      format: "a4"
    });
    const title = "Staff Attendance Logs — " + organisationName;
    doc.setFontSize(14);
    doc.text(title, 40, 40);
    const filters = [selectedPharmacy === "All" ? "Pharmacy: All Pharmacies" : "Pharmacy: " + selectedPharmacy, "Month: " + monthName, "Year: " + yearLabel, selectedStaff === "All" ? null : "Staff: " + selectedStaff, searchText ? 'Search: "' + searchText + '"' : null, showDetails ? "Detail: full" : "Detail: totals only", onlyShowIssues ? "Filter: only issues" : null].filter(Boolean).join("   |   ");
    doc.setFontSize(10);
    doc.text(filters, 40, 58);
    const head = [["Date", "Pharmacy", "Name", "Session", "Hrs worked", "Notes"]];
    const body = flat.map(r => [r.date, r.pharmacy, r.name, r.session, r.hours, r.notes]);
    doc.autoTable({
      head,
      body,
      startY: 72,
      styles: {
        fontSize: 9,
        cellPadding: 6,
        overflow: "linebreak"
      },
      columnStyles: {
        0: {
          cellWidth: 140
        },
        1: {
          cellWidth: 120
        },
        2: {
          cellWidth: 140
        },
        3: {
          cellWidth: 110
        },
        4: {
          cellWidth: 90
        },
        5: {
          cellWidth: 200
        }
      },
      didParseCell: data => {
        const rowObj = flat[data.row.index];
        if (!rowObj) return;
        if (rowObj._type === "divider") {
          if (data.column.index === 0) {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.halign = "center";
          }
          data.cell.styles.fillColor = [248, 250, 252];
        }
        if (rowObj._type !== "divider" && data.section === "body" && data.column.index === 4) {
          const txt = String(data.cell.raw || "");
          const numeric = /^-?\d+(\.\d+)?$/.test(txt) && parseFloat(txt) > 0;
          if (!numeric) data.cell.styles.fillColor = [255, 182, 193];
        }
      },
      theme: "grid",
      margin: {
        left: 40,
        right: 40
      },
      headStyles: {
        fillColor: [238, 242, 247]
      }
    });
    doc.save(`Staff_Attendance_${organisationName}_${monthName}_${yearLabel}.pdf`);
  };

  /* ========== Export: Excel (enhanced with breaks and totals) ========== */
  const sanitizeSheetName = name => {
    const bad = /[\\\/?*\[\]:]/g;
    const base = String(name || "Sheet").replace(bad, "-").trim() || "Sheet";
    return base.slice(0, 31);
  };
  const uniqueSheetName = (base, used) => {
    let name = sanitizeSheetName(base);
    if (!used.has(name)) {
      used.add(name);
      return name;
    }
    for (let i = 2; i < 1000; i++) {
      const tryName = sanitizeSheetName(base + " " + i);
      if (!used.has(tryName)) {
        used.add(tryName);
        return tryName;
      }
    }
    const fallback = sanitizeSheetName(base + " " + Date.now().toString().slice(-5));
    used.add(fallback);
    return fallback;
  };
  const exportToExcel = () => {
    if (selectedMonth === "All" || selectedYear === "All") {
      appAlert("Please select a specific Month and Year before exporting to Excel.");
      return;
    }
    const reportDay = getTimesheetDay();
    const yearNum = parseInt(selectedYear, 10);
    const monthIdx = parseInt(selectedMonth, 10);
    const {
      start,
      end
    } = anchoredWindowForSelection(yearNum, monthIdx, reportDay);
    const startBuffered = new Date(start.getTime() - BUFFER_MS);
    const endBuffered = new Date(end.getTime() + BUFFER_MS);
    const logsInWindow = data.filter(item => {
      const created = item.createdAt ? new Date(item.createdAt) : null;
      if (!created || isNaN(created.getTime())) return false;
      if (!(created >= startBuffered && created < endBuffered)) return false;
      if (selectedPharmacy !== "All" && item.pharmacyName !== selectedPharmacy) return false;
      return true;
    });
    if (logsInWindow.length === 0) {
      appAlert("No data for the selected month/year and filters.");
      return;
    }
    const byStaff = new Map();
    for (const it of logsInWindow) {
      const staff = it.staffName || "Unknown";
      if (!byStaff.has(staff)) byStaff.set(staff, []);
      byStaff.get(staff).push(it);
    }
    const staffList = Array.from(byStaff.entries()).map(([staff, items]) => {
      const counts = {};
      for (const it of items) counts[it.pharmacyName || "Unknown"] = (counts[it.pharmacyName || "Unknown"] || 0) + 1;
      const primaryPharmacy = Object.keys(counts).sort((a, b) => counts[b] - counts[a] || a.localeCompare(b))[0] || "";
      items.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      return {
        staff,
        items,
        primaryPharmacy
      };
    });
    staffList.sort((A, B) => {
      const pa = (A.primaryPharmacy || "").toLowerCase();
      const pb = (B.primaryPharmacy || "").toLowerCase();
      if (pa < pb) return -1;
      if (pa > pb) return 1;
      return (A.staff || "").toLowerCase().localeCompare((B.staff || "").toLowerCase());
    });
    const wb = XLSX.utils.book_new();
    const usedSheetNames = new Set();
    const headerRow = ["Date", "Pharmacy", "Name", "Clock in", "Clock out", "On site (h)", "Breaks (h)", "Worked (h)", "Break details", "Notes", "Distance (m)", "Overtime (h)", "OT approved", "Reviewed", "Manager notes"];
    const fileMonth = new Date(0, monthIdx).toLocaleString("default", {
      month: "long"
    });
    const fileYear = selectedYear;
    const startMonthName = start.toLocaleString("default", {
      month: "long"
    });
    const startYear = start.getFullYear();
    staffList.forEach(({
      staff,
      items,
      primaryPharmacy
    }) => {
      const sessions = items.map(e => ({
        in: e,
        out: e
      }));
      const kept = sessions.filter(s => {
        const anchor = s.in?.clockIn ? new Date(s.in.clockIn) : s.out?.clockOut ? new Date(s.out.clockOut) : new Date(s.in?.createdAt || s.out?.createdAt || Date.now());
        return anchor >= start && anchor < end;
      });
      const bucket = new Map();
      for (const s of kept) {
        const anchor = s.in?.clockIn ? new Date(s.in.clockIn) : s.out?.clockOut ? new Date(s.out.clockOut) : new Date(s.in?.createdAt || s.out?.createdAt || Date.now());
        const key = anchor.toISOString().slice(0, 10);
        (bucket.get(key) || bucket.set(key, []).get(key)).push(s);
      }
      const aoa = [];
      aoa.push(["Group: " + organisationName]);
      aoa.push(["Generated: " + generatedAt]);
      aoa.push(["Staff: " + staff + (primaryPharmacy ? " (" + primaryPharmacy + ")" : "")]);
      aoa.push(["Period: " + reportDay + " " + startMonthName + " " + startYear + " -> " + reportDay + " " + fileMonth + " " + fileYear]);
      aoa.push([]);
      aoa.push(headerRow);
      let sumWorked = 0;
      let sumBreaks = 0;
      let sumOnsite = 0;
      let anyNK = false;
      const dateKeys = Array.from(bucket.keys()).sort((a, b) => new Date(a) - new Date(b));
      dateKeys.forEach(dateStr => {
        const dayOfWeek = new Date(dateStr).toLocaleString("en-GB", {
          weekday: "short"
        });
        const list = bucket.get(dateStr) || [];
        let dayWorked = 0;
        let dayBreaks = 0;
        let dayOnsite = 0;
        const distPairs = [];

        // day-level annotations for the four new columns (first session row)
        const xSid = String(list.map(s => s.in?.staffId || s.out?.staffId).find(Boolean) || "");
        const xPh = list.map(s => s.in?.pharmacyName || s.out?.pharmacyName).find(Boolean) || primaryPharmacy || "";
        let xOnsite = 0;
        for (const s of list) {
          if (s.in?.clockIn && s.out?.clockOut) {
            const a2 = new Date(s.in.clockIn),
              b2 = new Date(s.out.clockOut);
            if (a2.toISOString().slice(0, 10) === b2.toISOString().slice(0, 10)) xOnsite += Math.max(0, (b2 - a2) / 3600000);
          }
        }
        const xAnno = annoFor(xPh, xSid, dateStr);
        const xOt = annoOvertimeH(xPh, xSid, dateStr, xOnsite);
        let firstSessionRow = true;
        list.forEach(({
          in: entry
        }) => {
          const entryIn = entry;
          const entryOut = entry;
          const cin = entryIn?.clockIn ? fmtHM(entryIn.clockIn) : "";
          const cout = entryOut?.clockOut ? fmtHM(entryOut.clockOut) : "";
          const attId = String(entryIn?._id || entryOut?._id || "");
          const breaks = attId && breaksByAttId[attId] ? breaksByAttId[attId] : [];
          let breaksHours = 0;
          let breaksList = [];
          for (const b of breaks) {
            const bs = b.breakStart ? new Date(b.breakStart) : null;
            const be = b.breakEnd ? new Date(b.breakEnd) : null;
            breaksList.push((bs ? fmtHM(bs) : "?") + "-" + (be ? fmtHM(be) : "?") + (b.notes ? " " + b.notes : ""));
            if (bs && be) {
              const dh = hoursDiff(bs, be);
              if (dh > 0) breaksHours += dh;
            }
          }
          let onsiteH = 0,
            workedH = 0;
          let hrsStrWorked = "n/k";
          if (entryIn?.clockIn && entryOut?.clockOut) {
            const inDay = new Date(entryIn.clockIn).toISOString().slice(0, 10);
            const outDay = new Date(entryOut.clockOut).toISOString().slice(0, 10);
            if (inDay !== outDay) {
              anyNK = true;
            } else {
              onsiteH = hoursDiff(entryIn.clockIn, entryOut.clockOut);
              workedH = Math.max(0, onsiteH - breaksHours);
              hrsStrWorked = workedH.toFixed(2);
              dayWorked += workedH;
              dayBreaks += breaksHours;
              dayOnsite += onsiteH;
            }
          } else {
            anyNK = true;
          }
          const lines = [...(entryIn?.clockInnotes ? String(entryIn.clockInnotes).split("\n") : []), ...(entryOut?.clockOutnotes ? String(entryOut.clockOutnotes).split("\n").map(n => "... " + n) : [])].map(s => s.trim()).filter(Boolean);
          const deduped = Array.from(new Set(lines)).join("\n");
          const phLat = toNumOrNull(entryIn?.pharmacyLat ?? entryOut?.pharmacyLat);
          const phLong = toNumOrNull(entryIn?.pharmacyLong ?? entryOut?.pharmacyLong);
          const inDist = entryIn && phLat != null && phLong != null && entryIn.clockInlat != null && entryIn.clockInlong != null ? haversineMeters(entryIn.clockInlat, entryIn.clockInlong, phLat, phLong) : null;
          const outDist = entryOut && phLat != null && phLong != null && entryOut.clockOutlat != null && entryOut.clockOutlong != null ? haversineMeters(entryOut.clockOutlat, entryOut.clockOutlong, phLat, phLong) : null;
          const distText = ("In: " + (inDist == null ? "" : inDist + " m") + "  Out: " + (outDist == null ? "" : outDist + " m")).trim();
          aoa.push([new Date(dateStr).toLocaleDateString("en-GB") + " (" + dayOfWeek + ")", entryIn?.pharmacyName || entryOut?.pharmacyName || primaryPharmacy || "", staff, cin, cout, onsiteH ? onsiteH.toFixed(2) : "", breaksHours ? breaksHours.toFixed(2) : "", hrsStrWorked, breaksList.join("; "), deduped, distText, firstSessionRow && xOt > 0 ? xOt.toFixed(2) : "", firstSessionRow && xOt > 0 ? xAnno.otApproved ? "Yes" : "No" : "", firstSessionRow ? xAnno.reviewed ? "Yes" : "" : "", firstSessionRow ? xAnno.notes || "" : ""]);
          firstSessionRow = false;
        });

        // Per-day subtotal
        if (list.length > 0) {
          sumWorked += dayWorked;
          sumBreaks += dayBreaks;
          sumOnsite += dayOnsite;
          aoa.push(["Day subtotal", "", "", "", "", dayOnsite.toFixed(2), dayBreaks.toFixed(2), dayWorked.toFixed(2), "", "", "", "", "", "", ""]);
          aoa.push([]);
        }
      });

      // Period total
      aoa.push(["Period totals", "", "", "", "", sumOnsite.toFixed(2), sumBreaks.toFixed(2), anyNK ? "n/k" : sumWorked.toFixed(2), "", "", "", "", "", "", ""]);
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      // Enable wrap text for Notes column (column J = index 9)
      Object.keys(ws).forEach(cell => {
        if (cell.startsWith("J")) {
          ws[cell].s = ws[cell].s || {};
          ws[cell].s.alignment = {
            wrapText: true
          };
        }
      });
      ws["!cols"] = [{
        wch: 24
      }, {
        wch: 24
      }, {
        wch: 26
      }, {
        wch: 10
      }, {
        wch: 10
      }, {
        wch: 12
      }, {
        wch: 12
      }, {
        wch: 12
      }, {
        wch: 30
      }, {
        wch: 40
      }, {
        wch: 22
      }];
      const sheetName = uniqueSheetName(staff || "Unknown", usedSheetNames);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });
    const filename = ("Staff_Attendance_" + organisationName + "_" + fileMonth + "_" + fileYear + ".xlsx").replace(/\s+/g, "_");
    const wbout = XLSX.write(wb, {
      type: "array",
      bookType: "xlsx"
    });
    // PIN-protected download (Settings → Organisation → export PIN).
    saveXlsxProtected(wbout, filename);
  };

  /* ========== Backend persist (unchanged) ========== */
  async function persistAttendanceUpdates(updates, audit) {
    const authToken = localStorage.getItem("authToken") || localStorage.getItem("token");
    const res = await fetch(API_BASE + "/updateStaffAttendanceLogs", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(authToken ? {
          Authorization: "Bearer " + authToken
        } : {})
      },
      body: JSON.stringify({
        updates,
        audit
      })
    });
    const raw = await res.text();
    if (!res.ok) throw new Error("Save failed: " + res.status + " " + raw);
    return raw ? JSON.parse(raw) : {};
  }

  /* ========== Edit (unchanged) ========== */
  const toDatetimeLocal = dLike => {
    if (!dLike) return "";
    const d = dLike instanceof Date ? dLike : new Date(dLike);
    if (isNaN(d)) return "";
    return d.toISOString().slice(0, 16);
  };
  const parseDatetimeLocal = s => {
    if (!s) return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})T([0-2]\d):([0-5]\d)$/.exec(s.trim());
    if (!m) return null;
    const [, yy, mm, dd, hh, min] = m;
    return new Date(Date.UTC(+yy, +mm - 1, +dd, +hh, +min, 0, 0));
  };
  const openEditForSession = (row, sessionIdx) => {
    if (!row || row.date === "Totals") return;
    const entryIn = row.rawEntries[sessionIdx] || null;
    const entryOut = row.rawEntries[sessionIdx] || null;
    const inStr = entryIn?.clockIn ? toDatetimeLocal(entryIn.clockIn) : "";
    const outStr = entryOut?.clockOut ? toDatetimeLocal(entryOut.clockOut) : "";
    const lines = [...(entryIn?.notes ? String(entryIn.notes).split("\n") : []), ...(entryOut?.notes ? String(entryOut.notes).split("\n").map(n => "... " + n) : [])].map(s => s.trim()).filter(Boolean);
    const deduped = Array.from(new Set(lines)).join(" ");
    setEditRow(row);
    setEditSessionIndex(sessionIdx);
    setEditInDT(inStr);
    setEditOutDT(outStr);
    setEditNotes(deduped);
    setEditAddNotes("");
    setEditOutEditable(Boolean(entryOut));
    setEditOpen(true);
  };
  const closeEdit = () => {
    setEditOpen(false);
    setEditRow(null);
    setEditSessionIndex(-1);
    setEditInDT("");
    setEditOutDT("");
    setEditNotes("");
    setEditAddNotes("");
    setEditOutEditable(true);
  };
  const applyEdits = async () => {
    if (!editRow || !editRow.rawEntries || editSessionIndex < 0) return;
    const entryIn = editRow.rawEntries[editSessionIndex] || null;
    const entryOut = editRow.rawEntries[editSessionIndex] || null;
    const changes = [];
    const newData = data.map(d => ({
      ...d
    }));
    const updates = [];
    const newIn = parseDatetimeLocal(editInDT);
    const newOut = parseDatetimeLocal(editOutDT);
    const updateLocalDoc = (orig, fields) => {
      const idx = newData.findIndex(x => matchesOriginal(x, orig));
      if (idx >= 0) newData[idx] = {
        ...newData[idx],
        ...fields
      };
      if (orig?._id && orig?._dbName && Object.keys(fields).length > 0) {
        updates.push({
          _id: String(orig._id),
          _dbName: String(orig._dbName),
          set: fields
        });
      }
    };
    if (entryIn) {
      const oldIn = entryIn.clockIn ? new Date(entryIn.clockIn) : null;
      const inChanged = Boolean(oldIn && newIn && oldIn.getTime() !== newIn.getTime() || !!oldIn !== !!newIn);
      if (inChanged) {
        const oldStr = oldIn ? oldIn.toISOString().slice(11, 16) : "—";
        const newStr = newIn ? newIn.toISOString().slice(11, 16) : "—";
        changes.push("Session #" + (editSessionIndex + 1) + " clock-in " + oldStr + " -> " + newStr);
        updateLocalDoc(entryIn, {
          clockIn: newIn ? newIn.toISOString() : null,
          createdAt: newIn ? newIn.toISOString() : entryIn.createdAt
        });
      }
    }
    if (entryOut) {
      const oldOut = entryOut.clockOut ? new Date(entryOut.clockOut) : null;
      const outChanged = Boolean(oldOut && newOut && oldOut.getTime() !== newOut.getTime() || !!oldOut !== !!newOut);
      if (outChanged) {
        const oldStr = oldOut ? oldOut.toISOString().slice(11, 16) : "—";
        const newStr = newOut ? newOut.toISOString().slice(11, 16) : "—";
        changes.push("Session #" + (editSessionIndex + 1) + " clock-out " + oldStr + " -> " + newStr);
        updateLocalDoc(entryOut, {
          clockOut: newOut ? newOut.toISOString() : null
        });
      }
    }
    const appendStr = editAddNotes.trim();
    const timestamp = new Date().toLocaleString("en-GB");
    if (appendStr) {
      if (entryIn && entryIn._id && entryIn._dbName) {
        updates.push({
          _id: String(entryIn._id),
          _dbName: String(entryIn._dbName),
          appendNotes: appendStr
        });
        const idxIn = newData.findIndex(x => matchesOriginal(x, entryIn));
        if (idxIn >= 0) {
          const prev = newData[idxIn].notes || "";
          const sep = prev ? "\n" : "";
          newData[idxIn] = {
            ...newData[idxIn],
            notes: prev + sep + timestamp + "\n" + appendStr
          };
        }
      }
      if (entryOut && entryOut._id && entryOut._dbName) {
        updates.push({
          _id: String(entryOut._id),
          _dbName: String(entryOut._dbName),
          appendNotes: appendStr
        });
        const idxOut = newData.findIndex(x => matchesOriginal(x, entryOut));
        if (idxOut >= 0) {
          const prev = newData[idxOut].notes || "";
          const sep = prev ? "\n" : "";
          newData[idxOut] = {
            ...newData[idxOut],
            notes: prev + sep + timestamp + "\n" + appendStr
          };
        }
      }
      changes.push("Notes appended for session #" + (editSessionIndex + 1));
    }
    setData(newData);
    try {
      if (updates.length > 0) {
        const audit = {
          staffName: editRow.staffName,
          pharmacyName: editRow.pharmacyName,
          originalDate: editRow.date,
          sessionIndex: editSessionIndex,
          changes,
          editorUserId: localStorage.getItem("userId") || "",
          editedAt: new Date().toISOString()
        };
        await persistAttendanceUpdates(updates, audit);
      }
      await fetchAllLogs();
      const summary = changes.length ? changes.join("; ") : "No changes.";
      setLastChangeSummary(editRow.staffName + " — " + summary);
    } catch (e) {
      console.error(e);
      (await appAlert("Failed to save changes. Please try again."));
      await fetchAllLogs();
    }
    closeEdit();
  };

  /* ========== Hover card helpers ========== */
  const buildHoverInfo = (row, sessionIdx) => {
    if (!row || row.date === "Totals") return null;
    const ein = row.rawEntries[sessionIdx] || null;
    const eout = row.rawEntries[sessionIdx] || null;
    const inTime = ein?.clockIn ? new Date(ein.clockIn) : null;
    const outTime = eout?.clockOut ? new Date(eout.clockOut) : null;
    const attId = String(ein?._id || eout?._id || "");
    const brs = attId && breaksByAttId[attId] ? breaksByAttId[attId] : [];
    let breaks = [];
    let breaksH = 0;
    for (let k = 0; k < brs.length; k++) {
      const bs = brs[k].breakStart ? new Date(brs[k].breakStart) : null;
      const be = brs[k].breakEnd ? new Date(brs[k].breakEnd) : null;
      const dh = bs && be ? hoursDiff(bs, be) : 0;
      if (dh > 0) breaksH += dh;
      breaks.push({
        idx: k + 1,
        start: bs ? fmtHM(bs) : "?",
        end: be ? fmtHM(be) : "?",
        hours: dh > 0 ? dh.toFixed(2) : "",
        notes: brs[k].notes || ""
      });
    }
    const onsiteH = inTime && outTime ? hoursDiff(inTime, outTime) : 0;
    const workedH = Math.max(0, onsiteH - breaksH);
    const inNotes = ein?.notes ? String(ein.notes) : "";
    const outNotes = eout?.notes ? String(eout.notes) : "";
    const combinedNotes = [inNotes, outNotes].filter(Boolean).join("\n");
    return {
      inStr: inTime ? fmtHM(inTime) : "—",
      outStr: outTime ? fmtHM(outTime) : "—",
      onsite: onsiteH > 0 ? onsiteH.toFixed(2) : "0.00",
      breakTotal: breaksH > 0 ? breaksH.toFixed(2) : "0.00",
      worked: workedH > 0 ? workedH.toFixed(2) : "0.00",
      breaks,
      notes: combinedNotes
    };
  };

  /* ========== UI ========== */
  const yearsList = Array.from({
    length: 10
  }, (_, i) => String(new Date().getFullYear() - i));
  return <div className="stafflogin-container">
      <style>{staffLogsStyles}</style>

      <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center"
    }}>
        <h2>Staff Attendance Logs{loading && <span style={{
          fontSize: 14,
          fontWeight: 500,
          color: "#0E8CFF",
          marginLeft: 12
        }}>Loading…</span>}</h2>
        <MaskToggleButton />
      </div>

      <div className="filters-row" style={{
      textAlign: "left",
      display: "flex",
      gap: 12,
      flexWrap: "wrap",
      alignItems: "end"
    }}>
        <label>
          Pharmacy
          <select value={selectedPharmacy} onChange={e => setSelectedPharmacy(e.target.value)}>
            <option value="All">All Pharmacies</option>
            {members.map((m, i) => <option key={i} value={m.pharmacyName}>{displayName(m.pharmacyName, masked)}</option>)}
          </select>
        </label>

        <label>
          Month
       <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}>
  <option value="All">All months</option>
  {Array.from({
            length: 12
          }, (_, i) => <option key={i} value={String(i + 1)}>
      {new Date(0, i).toLocaleString("default", {
              month: "long"
            })}
    </option>)}
</select>

        </label>

        <label>
          Year
          <select value={selectedYear} onChange={e => setSelectedYear(e.target.value)}>
            <option value="All">All years</option>
            {yearsList.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>

        <label className="checkbox-label" style={{
        display: "flex",
        gap: 6,
        alignItems: "center"
      }}>
          <input type="checkbox" checked={showDetails} onChange={e => setShowDetails(e.target.checked)} />
          Show details
        </label>

        <label className="checkbox-label" style={{
        display: "flex",
        gap: 6,
        alignItems: "center"
      }}>
          <input type="checkbox" checked={onlyShowIssues} onChange={e => setOnlyShowIssues(e.target.checked)} />
          Only show issues (invalid hours or notes)
        </label>

        <label>
          Staff name
          <select value={selectedStaff} onChange={e => setSelectedStaff(e.target.value)}>
            <option value="All">All</option>
            {staffNameOptions.map(n => <option key={n} value={n}>{displayName(n, masked)}</option>)}
          </select>
        </label>

        <label style={{
        marginLeft: "auto"
      }}>
          Search
          <input type="text" value={searchText} onChange={e => setSearchText(e.target.value)} placeholder="Search..." />
        </label>

        <div style={{
        display: "flex",
        gap: 8
      }}>
          {/* Export to Word removed per request — the function remains below.
           <button onClick={exportToWord}>Export to Word</button> */}
          {false && <button onClick={exportToPDF}>Export to PDF</button>}
          <button onClick={exportToExcel}>Export to Excel</button>
        </div>
      </div>

      {lastChangeSummary && <div style={{
      marginTop: 8,
      padding: "8px 10px",
      background: "#f0f9ff",
      border: "1px solid #bee3f8",
      borderRadius: 6
    }}>
          <strong>Last change:</strong> {lastChangeSummary}
        </div>}

      <div className="table-wrapper" style={{
      marginTop: 8
    }}>
        <table className="data-table staff-logs">
          <colgroup>
            <col style={{
            width: "150px"
          }} />
            <col style={{
            width: "180px"
          }} />
            <col style={{
            width: "220px"
          }} />
            <col style={{
            width: "220px"
          }} />
            <col style={{
            width: "180px"
          }} />
            <col style={{
            width: "300px"
          }} />
            <col style={{
            width: "220px"
          }} />
          </colgroup>
          <thead>
            <tr>
              <th onClick={() => handleSortClick("date")}><span className="th-wrap"><span className="label">Date</span>{sortArrow("date")}</span></th>
              <th onClick={() => handleSortClick("pharmacyName")}><span className="th-wrap"><span className="label">Pharmacy</span>{sortArrow("pharmacyName")}</span></th>
              <th onClick={() => handleSortClick("staffName")}><span className="th-wrap"><span className="label">Name</span>{sortArrow("staffName")}</span></th>
              <th className="nosort">Sessions</th>
              <th className="nosort">Hrs worked</th>
              <th className="nosort">Notes</th>
              <th className="nosort">Distance</th>
              <th className="nosort">Overtime (h)</th>
              <th className="nosort">OT approved</th>
              <th className="nosort">Reviewed</th>
              <th className="nosort">Manager notes</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
            const out = [];
            for (let idx = 0; idx < visibleRows.length; idx++) {
              const row = visibleRows[idx];
              if (row.date === "Totals") {
                const highlight = row.incomplete;
                out.push(<tr key={`totals-${idx}`} data-highlight={highlight ? "true" : "false"}>
                      <td>Totals</td><td></td><td>{displayName(row.staffName, masked)}</td><td></td><td>{row.totalHours}</td><td></td><td></td><td></td><td></td><td></td><td></td>
                    </tr>);
                out.push(<tr key={`divider-${idx}`} className="divider-row">
                      <td colSpan={11}>—— End of {displayName(row.staffName, masked)} entry ——</td>
                    </tr>);
                continue;
              }
              const n = Math.max(1, row.sessions.length);
              for (let i = 0; i < n; i++) {
                const segClass = i > 0 ? "seg" : "";
                const hoursVal = row.hoursWorkedPerSession[i] ?? "";
                const invalid = isInvalidHoursVal(hoursVal);
                const edited = row.notesEditedFlags[i];
                const pair = row.distancePairs?.[i] || {
                  in: null,
                  out: null
                };
                const over50 = Number(pair.in) >= 50 || Number(pair.out) >= 50;
                const distClass = over50 ? "dist-coral" : "";
                const key = idx + ":" + i;
                out.push(<tr key={`${idx}-seg-${i}`} className={invalid ? "row-invalid" : ""}>
                      {i === 0 && <><td rowSpan={n}>{new Date(row.date).toLocaleDateString("en-GB")} ({row.dayOfWeek})</td>
                                     <td rowSpan={n}>{displayName(row.pharmacyName, masked)}</td>
                                     <td rowSpan={n}>{displayName(row.staffName, masked)}</td></>}
                      <td className={segClass}>
                        <div className="session-wrap" onMouseEnter={() => {
                      setHoverKey(key);
                      setHoverInfo(buildHoverInfo(row, i));
                    }} onMouseLeave={() => {
                      setHoverKey(null);
                      setHoverInfo(null);
                    }}>
                          <span>{row.sessions[i] || ""}</span>
                          {hoverKey === key && hoverInfo && <div className="hover-card">
                              <div className="hover-row"><span className="hover-title">Clock in</span><span>{hoverInfo.inStr}</span></div>
                              <div className="hover-row"><span className="hover-title">Clock out</span><span>{hoverInfo.outStr}</span></div>
                              {hoverInfo.breaks.map(b => <div key={b.idx} className="hover-row">
                                  <span>{"Break " + b.idx + (b.notes ? " (" + b.notes + ")" : "")}</span>
                                  <span>{b.start + " - " + b.end + (b.hours ? "  " + b.hours + "h" : "")}</span>
                                </div>)}
                              <div className="hover-total">
                                <div className="hover-row"><span>Total on site</span><span>{hoverInfo.onsite} h</span></div>
                                <div className="hover-row"><span>Total breaks taken</span><span>{hoverInfo.breakTotal} h</span></div>
                                <div className="hover-row"><span>Total hours worked</span><span>{hoverInfo.worked} h</span></div>
                                {hoverInfo.notes ? <div className="hover-note">{hoverInfo.notes}</div> : null}
                              </div>
                            </div>}
                        </div>
                      </td>
                      <td className={segClass + " " + (hoursVal === "n/k" ? "nk" : "")}>{hoursVal}</td>
                      <td className={segClass + " " + (edited ? "amber" : "")}>
                        {(row.notesBlocks[i] || "").split("\n").map((ln, k) => <div key={k}>{ln}</div>)}
                      </td>
                      <td className={segClass + " " + distClass + " dist-cell"} title="Click to view map" onClick={() => openMapForSession(row, i)}>
                        {"In: " + (pair.in == null ? "—" : pair.in + " m") + "  Out: " + (pair.out == null ? "—" : pair.out + " m")}
                      </td>
                      {i === 0 && <>
                        <td rowSpan={n} style={{
                      color: "#0958D9",
                      fontWeight: 600
                    }}>{row.overtimeH || ""}</td>
                        <td rowSpan={n}>{row.otApproved ? "Yes" : ""}</td>
                        <td rowSpan={n} style={{
                      color: "#1e40af"
                    }}>{row.reviewed ? "✔ Yes" : ""}</td>
                        <td rowSpan={n} style={{
                      maxWidth: 220,
                      whiteSpace: "pre-wrap"
                    }}>{row.reviewNotes || ""}</td>
                      </>}
                    </tr>);
              }
            }
            return out;
          })()}
            {loading && visibleRows.length === 0 && <tr><td colSpan={11} style={{
              color: "#0E8CFF",
              fontWeight: 600
            }}>Loading… fetching attendance logs for all pharmacies — this can take a few seconds.</td></tr>}
            {!loading && visibleRows.length === 0 && <tr><td colSpan={11}>No data.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* EDIT MODAL */}
      {ALLOW_EDIT && editOpen && editRow && editSessionIndex >= 0 && <div className="modal-backdrop" onClick={e => {
      if (e.target.classList.contains("modal-backdrop")) closeEdit();
    }}>
          <div className="modal-card">
            <div className="modal-header">
              <h3 className="modal-title">
                Edit Session #{editSessionIndex + 1} — {displayName(editRow.staffName, masked)}
              </h3>
              <button className="btn" onClick={closeEdit}>Close</button>
            </div>
            <div className="modal-body">
              <div className="episode">
                <div style={{
              fontWeight: 600,
              marginBottom: 6
            }}>Clock-in / Clock-out</div>
                <div className="row-grid" style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12
            }}>
                  <div>
                    <label>Clock-in (date & time)</label>
                    <input type="datetime-local" value={editInDT} onChange={e => setEditInDT(e.target.value)} />
                  </div>
                  <div>
                    <label>Clock-out (date & time)</label>
                    <input type="datetime-local" value={editOutDT} onChange={e => setEditOutDT(e.target.value)} disabled={!editOutEditable} />
                    {!editOutEditable && <div style={{
                  fontSize: 12,
                  color: "#5D5D5D",
                  marginTop: 4
                }}>
                        Clock out awaited — no entry exists yet.
                      </div>}
                  </div>
                </div>
              </div>

              <div className="field" style={{
            marginTop: 12
          }}>
                <label>Current note</label>
                <textarea rows={4} value={editNotes} readOnly style={{
              width: "100%",
              resize: "vertical",
              background: "#FAFAFA"
            }} />
              </div>
              <div className="field">
                <label>Add note</label>
                <textarea rows={3} value={editAddNotes} onChange={e => setEditAddNotes(e.target.value)} placeholder="Type an additional note to append…" style={{
              width: "100%",
              resize: "vertical"
            }} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={closeEdit}>Cancel</button>
              <button className="btn primary" onClick={applyEdits}>Save changes</button>
            </div>
          </div>
        </div>}

      {/* MAP MODAL */}
      {mapOpen && mapInfo && <div className="map-backdrop" onClick={e => {
      if (e.target.classList.contains("map-backdrop")) setMapOpen(false);
    }}>
          <div className="map-card">
            <div className="map-header">
              <h3 className="map-title">Location — {mapInfo.label}</h3>
              <button className="btn" onClick={() => setMapOpen(false)}>Close</button>
            </div>
            <div className="map-body">
              <div style={{
            marginBottom: 8,
            display: "flex",
            alignItems: "center",
            gap: 16
          }}>
                <label style={{
              display: "flex",
              alignItems: "center",
              gap: 6
            }}>
                  <input type="radio" name="mapMode" value="in" checked={mapMode === "in"} onChange={() => setMapMode("in")} disabled={mapInfo.inLat == null || mapInfo.inLong == null} />
                  Clock in
                </label>
                <label style={{
              display: "flex",
              alignItems: "center",
              gap: 6
            }}>
                  <input type="radio" name="mapMode" value="out" checked={mapMode === "out"} onChange={() => setMapMode("out")} disabled={mapInfo.outLat == null || mapInfo.outLong == null} />
                  Clock out
                </label>
              </div>

              <div id="attendance-map" ref={mapDivRef} />
              <div style={{
            marginTop: 8,
            fontSize: 12,
            color: "#434343"
          }}>
                <strong>Legend:</strong> 🏠 Pharmacy  |  📍 {mapMode === "in" ? "IN " + mapInfo.inWhen : "OUT " + mapInfo.outWhen}
              </div>
            </div>
            <div className="map-footer">
              <button className="btn" onClick={() => setMapOpen(false)}>Close</button>
            </div>
          </div>
        </div>}
    </div>;
};

// No timezone adjustment — returns "HH:MM" from ISO string (UTC)
const formatHHMMNoTZ = dLike => {
  if (!dLike) return "00:00";
  const d = dLike instanceof Date ? dLike : new Date(dLike);
  if (isNaN(d)) return "00:00";
  return d.toISOString().slice(11, 16);
};
export default Staff;
